import { BlockGroup, WorkflowBlock } from '@/types/workflow';
import { ChevronDown, ChevronRight, Home } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import { BRAND_NAVY } from '@/constants/brandColors';

const NAVY = BRAND_NAVY;

interface LayerTreeProps {
  groups: BlockGroup[];
  blocks: WorkflowBlock[];
  currentGroupId: string | null;
  onNavigate: (groupId: string | null) => void;
}

export function LayerTree({ groups, blocks, currentGroupId, onNavigate }: LayerTreeProps) {
  const hasCollapsedGroups = groups.some(g => g.collapsed);
  if (!hasCollapsedGroups) return null;

  return (
    <div className="space-y-0.5">
      <button
        className={cn(
          "flex items-center gap-1.5 w-full px-2 py-1.5 rounded cursor-pointer transition-colors text-xs font-medium",
          currentGroupId === null
            ? "bg-brand-navy text-white"
            : "hover:bg-brand-navy/10"
        )}
        style={currentGroupId === null ? undefined : { color: NAVY }}
        onClick={() => onNavigate(null)}
      >
        <Home className="w-3 h-3 flex-shrink-0 opacity-60" />
        <span className="truncate">Root</span>
      </button>
      {groups
        .filter(g => g.collapsed && !g.parentGroupId)
        .map(group => (
          <TreeNode
            key={group.id}
            group={group}
            groups={groups}
            blocks={blocks}
            currentGroupId={currentGroupId}
            onNavigate={onNavigate}
            depth={1}
          />
        ))}
    </div>
  );
}

function TreeNode({
  group,
  groups,
  blocks,
  currentGroupId,
  onNavigate,
  depth,
}: {
  group: BlockGroup;
  groups: BlockGroup[];
  blocks: WorkflowBlock[];
  currentGroupId: string | null;
  onNavigate: (groupId: string | null) => void;
  depth: number;
}) {
  const [expanded, setExpanded] = useState(true);
  const children = groups.filter(g => g.collapsed && g.parentGroupId === group.id);
  const hasChildren = children.length > 0;
  const blockCount = group.blockIds.length;
  const isActive = currentGroupId === group.id;

  return (
    <div>
      <div
        className={cn(
          "flex items-center gap-1.5 w-full px-2 py-1.5 rounded cursor-pointer transition-colors text-xs font-medium",
          isActive ? "bg-brand-navy text-white" : "hover:bg-brand-navy/10"
        )}
        style={{ paddingLeft: `${8 + depth * 12}px`, ...(isActive ? {} : { color: NAVY }) }}
        onClick={() => onNavigate(group.id)}
      >
        {hasChildren ? (
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
            className="p-0.5 rounded hover:bg-brand-navy/10"
          >
            {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
        ) : (
          <span className="w-4" />
        )}
        <div
          className="w-2.5 h-2.5 rounded-full flex-shrink-0 ring-1 ring-brand-navy/20"
          style={{ backgroundColor: group.color }}
        />
        <span className="truncate">
          {group.name || 'Group'} ({blockCount})
        </span>
      </div>
      {hasChildren && expanded && (
        <div>
          {children.map(child => (
            <TreeNode
              key={child.id}
              group={child}
              groups={groups}
              blocks={blocks}
              currentGroupId={currentGroupId}
              onNavigate={onNavigate}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}
